import { useEffect, useState } from "react";
import { format } from "date-fns";
import { useStore } from "../../../store";
import Stars from "src/components/shared/stars";
import { formatCurrency } from "../../../utils/formatCurrency";
import { Button, Input } from "src/components/shared";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { addSessionToUser } from "src/store/session";

interface Props {
  email: string;
  price: number;
  setPrice: (price: number) => void;
}

const Coupon = ({ email, price, setPrice }: Props) => {
  const navigate = useNavigate();
  const { courseStore, userStore } = useStore();
  const session = courseStore.classDetails;

  const [showCoupon, setShowCoupon] = useState(false);
  const [code, setCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [clearCoupon, setClearCoupon] = useState(false);
  const [appliedCode, setAppliedCode] = useState("");
  const [couponValue, setCouponValue] = useState(0);

  const formatDate = (date: string) => {
    const [rawDate] = date?.split("T");
    const [year, month, day] = rawDate?.split("-");

    const pinnedDate = new Date(+year, +month - 1, +day);

    return format(pinnedDate, "dd MMM").toUpperCase();
  };

  const handleShowCoupon = () => {
    setShowCoupon(!showCoupon);
    setCouponApplied(false);
    let price = session?.price;
    if (session?.newSessionDiscount) {
      price = (session?.price * (100 - session?.newSessionDiscount)) / 100;
    }
    setPrice(price);
  };

  const useCode = async () => {
    if (clearCoupon) {
      setClearCoupon(false);
      setCode("");
      return;
    }

    if (!email) {
      toast.error("Please Enter your Email!");
      return;
    }

    let userId = userStore?.user?.id;
    if (!userStore?.user) {
      const tempUser = await userStore.createTempUser({ email });
      userId = tempUser.token.userId;
    }

    const response: any = await courseStore.subscribeWithCoupon({
      code: code.toLowerCase(),
      sessionId: session.id,
      studentId: userId,
    });

    if (response === null) {
      toast.error("Invalid Coupon!");
      return;
    }

    let updatedNewPrice = session?.price - response.missingAmount;
    setCouponValue(updatedNewPrice);
    updatedNewPrice = price - updatedNewPrice;
    setPrice(updatedNewPrice);
    setCouponApplied(true);
    setClearCoupon(true);
    setAppliedCode(code);
    toast.success("Coupon Applied Successfully!");

    // if (updatedNewPrice <= 0) {
    //   try {
    //     await addSessionToUser(session.id, userStore.user?.id);
    //     localStorage.setItem("registrationStep", "2");
    //     localStorage.setItem("registrationUser", JSON.stringify({ email }));
    //     navigate("/payment-success/" + session.id);
    //   } catch (err) {
    //     console.error("Error subscribing user automatically:", err);
    //     toast.error(
    //       "Unable to complete automatic subscription. Please try again."
    //     );
    //   }
    // }
  };

  return (
    <div
      className="flex flex-col gap-6 px-6 py-5 sm:px-[50px] sm:py-10 w-full h-fit sm:w-2/5 bg-white rounded-[30px]"
      style={{ boxShadow: "0px 18px 26px -4px #00000005" }}
    >
      <div>
        <p className="caption-text-bold text-primary-purple-600">
          {`${formatDate(session?.start)} - ${formatDate(session?.end)}`}
        </p>
        <h3 className="text-body-medium font-body-medium-bold sm:text-body-large sm:font-body-large-bold">
          {session.title}
        </h3>
        <div className="flex items-center gap-2.5">
          <span className="caption-text-bold text-primary-gray-500">
            {session?.rating}
          </span>
          <Stars rating={session?.rating}></Stars>
        </div>
        <span className="caption-text">by {session?.tutors?.fullName}</span>
      </div>

      {session?.price > 0 && (
        <div className="flex flex-col gap-3.5 sm:gap-6">
          <div className="body-small">
            <div className="flex items-center justify-between">
              <span className="opacity-60">Base Price</span>
              <span>{formatCurrency(session?.price, userStore?.currency)}</span>
            </div>
            {session?.newSessionDiscount ? (
              <>
                <div className="flex items-center justify-between">
                  <span className="opacity-60">
                    Discount <b>({session?.newSessionDiscount}% OFF)</b>
                  </span>
                  <span className="text-tertiary-success-600">
                    -
                    {formatCurrency(
                      (session?.price * session?.newSessionDiscount) / 100,
                      userStore?.currency
                    )}
                  </span>
                </div>
                {couponApplied && (
                  <div className="flex items-center justify-between">
                    <span className="opacity-60">
                      Coupon <b>({appliedCode})</b>
                    </span>
                    <span className="text-tertiary-success-600">
                      -{formatCurrency(couponValue, userStore?.currency)}
                    </span>
                  </div>
                )}
              </>
            ) : couponApplied ? (
              <div className="flex items-center justify-between">
                <span className="opacity-60">
                  Discount <b>({appliedCode})</b>
                </span>
                <span className="text-tertiary-success-600">
                  -{formatCurrency(couponValue, userStore?.currency)}
                </span>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <span className="opacity-60">Discount</span>
                <span className="opacity-60">
                  {formatCurrency(0, userStore?.currency)}
                </span>
              </div>
            )}
          </div>

          {session?.newSessionDiscount !== 100 && (
            <div className="flex flex-col gap-3">
              <Button
                text={showCoupon ? "Remove Coupon" : "Apply Coupon"}
                type={showCoupon ? "secondary" : "pale"}
                onClick={handleShowCoupon}
                customStyle={{
                  borderRadius: "999px",
                  padding: "0.5rem 1.5rem",
                }}
              />
              {showCoupon && (
                <div className="flex gap-1">
                  <Input
                    value={clearCoupon ? appliedCode : code}
                    onChange={(e) => setCode(e.target.value.toUpperCase())}
                    placeholder="Enter Code Here"
                    readOnly={clearCoupon}
                  />
                  <Button
                    text={clearCoupon ? "Clear" : "Apply"}
                    type={clearCoupon ? "pale" : "primary_dark"}
                    width="fit-content"
                    onClick={useCode}
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex items-end justify-between pt-6 border-t border-black border-opacity-5">
            <span className="body-medium">Total</span>
            <span className="body-large-bold">
              {price > 0 ? formatCurrency(price, userStore?.currency) : "FREE"}
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Coupon;
